import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { VanguardNav } from '../components/vanguard/VanguardNav';
import { LandingFooter } from '../components/landing/LandingFooter';
import { PageTransition } from '../components/common/PageTransition';

const LAST_UPDATED = '29 de maio de 2025';
const CONTROLLER_NAME = 'Pacces Advogados';
const CONTROLLER_CNPJ = '38.756.024/0001-48';
const CONTROLLER_ADDRESS = 'Av. Ordem e Progresso, 157, Sala 408, Barra Funda, São Paulo – SP';
const DPO_EMAIL = 'contato@paccesadvogados.com.br';
const DPO_PHONE = '(11) 2369-0371';

const sections = [
    { id: 'identificacao', label: '1. Identificação do Controlador' },
    { id: 'dados-coletados', label: '2. Dados Coletados' },
    { id: 'bases-legais', label: '3. Bases Legais' },
    { id: 'finalidades', label: '4. Finalidades do Tratamento' },
    { id: 'compartilhamento', label: '5. Compartilhamento de Dados' },
    { id: 'retencao', label: '6. Retenção e Eliminação' },
    { id: 'direitos', label: '7. Direitos do Titular' },
    { id: 'cookies', label: '8. Cookies' },
    { id: 'seguranca', label: '9. Segurança' },
    { id: 'transferencias', label: '10. Transferências Internacionais' },
    { id: 'menores', label: '11. Menores de Idade' },
    { id: 'atualizacoes', label: '12. Atualizações desta Política' },
    { id: 'contato', label: '13. Contato e Encarregado (DPO)' },
];

export const PrivacyPage = () => {
    return (
        <PageTransition>
            <Helmet>
                <title>Política de Privacidade | Pacces Advogados</title>
                <meta name="description" content="Política de Privacidade e Proteção de Dados da Pacces Advogados, em conformidade com a Lei Geral de Proteção de Dados (Lei nº 13.709/2018 – LGPD)." />
                <meta name="robots" content="index, follow" />
                <link rel="canonical" href="https://paccesadvogados.com.br/privacidade" />
            </Helmet>

            <div className="bg-white dark:bg-stone-950 min-h-[100dvh] font-vanguard-sans">
                <VanguardNav />

                {/* Hero */}
                <header className="bg-pacces-ebony border-b border-pacces-gold/10 pt-24 pb-16">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <nav aria-label="Caminho de navegação" className="flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.25em] text-stone-500 mb-8">
                            <Link to="/" className="hover:text-pacces-gold transition-colors">Início</Link>
                            <span className="opacity-30">/</span>
                            <span className="text-stone-400">Privacidade</span>
                        </nav>
                        <span className="text-pacces-gold text-[12px] font-bold uppercase tracking-[0.4em] block mb-4">
                            Proteção de Dados · LGPD
                        </span>
                        <h1 className="text-4xl md:text-5xl font-serif text-white leading-tight tracking-tight mb-4">
                            Política de Privacidade
                        </h1>
                        <p className="text-stone-400 text-base font-light">
                            Última atualização: {LAST_UPDATED}
                        </p>
                    </div>
                </header>

                {/* Content */}
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
                    <div className="lg:grid lg:grid-cols-12 lg:gap-16">

                        {/* Sidebar — índice */}
                        <aside className="hidden lg:block lg:col-span-3">
                            <div className="sticky top-28 space-y-1">
                                <p className="text-[12px] font-bold uppercase tracking-[0.3em] text-stone-400 mb-5">
                                    Índice
                                </p>
                                {sections.map((s) => (
                                    <a
                                        key={s.id}
                                        href={`#${s.id}`}
                                        className="block py-2 px-3 text-[12px] font-medium text-stone-500 dark:text-stone-400 hover:text-pacces-gold border-l-2 border-transparent hover:border-pacces-gold/50 transition-all duration-200"
                                    >
                                        {s.label}
                                    </a>
                                ))}
                            </div>
                        </aside>

                        {/* Artigo principal */}
                        <article className="lg:col-span-9 space-y-16 text-stone-600 dark:text-stone-400">

                            {/* Intro */}
                            <div className="prose prose-stone dark:prose-invert max-w-none prose-base">
                                <p className="text-lg text-stone-700 dark:text-stone-300 font-light leading-relaxed">
                                    A <strong className="font-semibold text-stone-900 dark:text-white">{CONTROLLER_NAME}</strong> respeita
                                    sua privacidade e está comprometida com a proteção dos seus dados pessoais em conformidade com a
                                    <strong className="font-semibold"> Lei Geral de Proteção de Dados Pessoais (LGPD – Lei nº 13.709/2018)</strong> e
                                    demais normas aplicáveis. Esta Política descreve como coletamos, usamos, armazenamos e protegemos
                                    suas informações quando você utiliza nosso site ou nos contata.
                                </p>
                            </div>

                            {/* 1. Identificação */}
                            <Section id="identificacao" title="1. Identificação do Controlador">
                                <p>
                                    O <strong className="text-stone-800 dark:text-stone-200">Controlador</strong> dos dados pessoais tratados
                                    por meio deste site é:
                                </p>
                                <InfoBox>
                                    <InfoRow label="Razão Social" value={CONTROLLER_NAME} />
                                    <InfoRow label="CNPJ" value={CONTROLLER_CNPJ} />
                                    <InfoRow label="Endereço" value={CONTROLLER_ADDRESS} />
                                    <InfoRow label="E-mail de Privacidade" value={DPO_EMAIL} />
                                    <InfoRow label="Telefone" value={DPO_PHONE} />
                                </InfoBox>
                            </Section>

                            {/* 2. Dados Coletados */}
                            <Section id="dados-coletados" title="2. Dados Coletados">
                                <p>Coletamos as seguintes categorias de dados pessoais:</p>
                                <SubSection title="2.1 Dados fornecidos voluntariamente">
                                    <ul className="space-y-2 list-none">
                                        <BulletItem>Nome completo</BulletItem>
                                        <BulletItem>Endereço de e-mail</BulletItem>
                                        <BulletItem>Número de telefone / WhatsApp</BulletItem>
                                        <BulletItem>Informações sobre sua demanda jurídica (resumo do caso, área de interesse)</BulletItem>
                                        <BulletItem>Endereço de e-mail para newsletter (quando fornecido)</BulletItem>
                                    </ul>
                                </SubSection>
                                <SubSection title="2.2 Dados coletados automaticamente">
                                    <ul className="space-y-2 list-none">
                                        <BulletItem>Endereço IP e dados de navegação (páginas acessadas, tempo de sessão)</BulletItem>
                                        <BulletItem>Tipo de dispositivo, navegador e sistema operacional</BulletItem>
                                        <BulletItem>Dados de interação via cookies e ferramentas de analytics (Google Analytics 4)</BulletItem>
                                        <BulletItem>Fonte de acesso (origem do tráfego)</BulletItem>
                                    </ul>
                                </SubSection>
                                <Note>
                                    Não coletamos dados pessoais sensíveis (art. 5º, II, LGPD) como origem racial, convicções
                                    religiosas, dados de saúde, biometria ou dados financeiros por meio deste site.
                                </Note>
                            </Section>

                            {/* 3. Bases Legais */}
                            <Section id="bases-legais" title="3. Bases Legais do Tratamento">
                                <p>
                                    Tratamos seus dados pessoais com fundamento nas seguintes hipóteses do
                                    <strong className="text-stone-800 dark:text-stone-200"> art. 7º da LGPD</strong>:
                                </p>
                                <div className="space-y-4 mt-6">
                                    <LegalBasis
                                        basis="Consentimento (art. 7º, I)"
                                        description="Para envio de comunicações de marketing e newsletter, quando você optar por recebê-las."
                                    />
                                    <LegalBasis
                                        basis="Execução de contrato ou diligências pré-contratuais (art. 7º, V)"
                                        description="Para responder às suas solicitações de consulta e viabilizar a prestação de serviços advocatícios."
                                    />
                                    <LegalBasis
                                        basis="Legítimo interesse do controlador (art. 7º, IX)"
                                        description="Para análise de uso do site, melhoria de funcionalidades, segurança da informação e prevenção a fraudes, observado o teste de balanceamento."
                                    />
                                    <LegalBasis
                                        basis="Cumprimento de obrigação legal ou regulatória (art. 7º, II)"
                                        description="Para atender exigências do Conselho Federal da OAB, obrigações fiscais e outras determinações legais aplicáveis à advocacia."
                                    />
                                </div>
                            </Section>

                            {/* 4. Finalidades */}
                            <Section id="finalidades" title="4. Finalidades do Tratamento">
                                <p>Seus dados são utilizados exclusivamente para as seguintes finalidades:</p>
                                <div className="mt-6 divide-y divide-stone-100 dark:divide-stone-800">
                                    <FinalidadeRow
                                        finalidade="Atendimento de consultas"
                                        descricao="Receber, analisar e responder suas solicitações de informação ou agendamento de consulta jurídica."
                                    />
                                    <FinalidadeRow
                                        finalidade="Comunicação via WhatsApp"
                                        descricao="Encaminhar sua mensagem ao advogado responsável pela área de interesse indicada."
                                    />
                                    <FinalidadeRow
                                        finalidade="Newsletter jurídica"
                                        descricao="Envio de atualizações legislativas, artigos e informativos, mediante consentimento prévio e revogável."
                                    />
                                    <FinalidadeRow
                                        finalidade="Analytics e melhoria do site"
                                        descricao="Mensurar o desempenho das páginas, identificar problemas técnicos e aprimorar a experiência de navegação."
                                    />
                                    <FinalidadeRow
                                        finalidade="Segurança e prevenção a fraudes"
                                        descricao="Detectar acessos indevidos, tentativas de ataque e assegurar a integridade da plataforma."
                                    />
                                    <FinalidadeRow
                                        finalidade="Cumprimento de obrigações legais"
                                        descricao="Manter registros exigidos pela legislação aplicável ao exercício da advocacia."
                                    />
                                </div>
                            </Section>

                            {/* 5. Compartilhamento */}
                            <Section id="compartilhamento" title="5. Compartilhamento de Dados">
                                <p>
                                    Não vendemos, alugamos ou cedemos seus dados pessoais a terceiros para fins comerciais.
                                    O compartilhamento ocorre somente nas hipóteses abaixo, de forma estritamente necessária:
                                </p>
                                <ul className="mt-4 space-y-3 list-none">
                                    <BulletItem><strong className="text-stone-700 dark:text-stone-300">Operadores de serviços:</strong> Ferramentas de analytics (Google Analytics), hospedagem de infraestrutura e serviços de comunicação (WhatsApp Business API), que atuam sob nosso mandato e estão sujeitos a obrigações contratuais de confidencialidade.</BulletItem>
                                    <BulletItem><strong className="text-stone-700 dark:text-stone-300">Autoridades públicas:</strong> Quando exigido por lei, ordem judicial, determinação de autoridade reguladora (ANPD, OAB) ou para exercício regular de direitos em processo judicial, arbitral ou administrativo (art. 7º, VI, LGPD).</BulletItem>
                                    <BulletItem><strong className="text-stone-700 dark:text-stone-300">Proteção de direitos:</strong> Quando necessário para prevenir fraudes, garantir a segurança do site ou proteger direitos, bens e interesses legítimos do escritório.</BulletItem>
                                </ul>
                            </Section>

                            {/* 6. Retenção */}
                            <Section id="retencao" title="6. Retenção e Eliminação dos Dados">
                                <p>
                                    Mantemos seus dados pessoais pelo tempo necessário ao cumprimento das finalidades que motivaram
                                    sua coleta, observando os seguintes critérios:
                                </p>
                                <div className="mt-6 space-y-4">
                                    <LegalBasis
                                        basis="Dados de contato e consultas"
                                        description="Retidos por até 5 (cinco) anos após o encerramento do relacionamento, conforme prazo prescricional geral do Código Civil (art. 205)."
                                    />
                                    <LegalBasis
                                        basis="Dados de navegação e analytics"
                                        description="Retidos por até 26 (vinte e seis) meses, conforme política padrão do Google Analytics 4."
                                    />
                                    <LegalBasis
                                        basis="Newsletter"
                                        description="Mantidos enquanto o consentimento estiver ativo. Eliminados imediatamente após revogação ou descadastramento."
                                    />
                                    <LegalBasis
                                        basis="Obrigações legais"
                                        description="Dados necessários ao cumprimento de obrigação legal ou regulatória serão mantidos pelo prazo exigido pela norma aplicável."
                                    />
                                </div>
                                <p className="mt-6">
                                    Findo o prazo de retenção, os dados serão eliminados de forma segura ou anonimizados, de modo
                                    que não possibilitem a identificação do titular.
                                </p>
                            </Section>

                            {/* 7. Direitos do Titular */}
                            <Section id="direitos" title="7. Direitos do Titular de Dados">
                                <p>
                                    Nos termos do <strong className="text-stone-800 dark:text-stone-200">art. 18 da LGPD</strong>, você tem
                                    direito de, a qualquer momento, solicitar ao controlador:
                                </p>
                                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <RightCard
                                        icon="verified_user"
                                        title="Confirmação e Acesso"
                                        description="Confirmar se tratamos seus dados e obter uma cópia das informações que possuímos sobre você."
                                    />
                                    <RightCard
                                        icon="edit"
                                        title="Correção"
                                        description="Solicitar a atualização ou retificação de dados incompletos, inexatos ou desatualizados."
                                    />
                                    <RightCard
                                        icon="block"
                                        title="Anonimização, Bloqueio ou Eliminação"
                                        description="Requerer a anonimização, bloqueio ou eliminação de dados desnecessários, excessivos ou tratados em desconformidade com a LGPD."
                                    />
                                    <RightCard
                                        icon="share"
                                        title="Portabilidade"
                                        description="Receber seus dados em formato estruturado e interoperável para transferência a outro fornecedor de serviço."
                                    />
                                    <RightCard
                                        icon="cancel"
                                        title="Eliminação por Consentimento"
                                        description="Solicitar a eliminação dos dados tratados com base em seu consentimento, salvo hipóteses legais de conservação."
                                    />
                                    <RightCard
                                        icon="info"
                                        title="Informação sobre Compartilhamento"
                                        description="Obter informação sobre as entidades públicas e privadas com as quais compartilhamos seus dados."
                                    />
                                    <RightCard
                                        icon="do_not_disturb"
                                        title="Recusa ao Consentimento"
                                        description="Ser informado sobre as consequências de não fornecer consentimento, quando o tratamento se basear nele."
                                    />
                                    <RightCard
                                        icon="undo"
                                        title="Revogação do Consentimento"
                                        description="Revogar o consentimento a qualquer tempo, de forma gratuita e facilitada, sem prejuízo ao tratamento já realizado."
                                    />
                                </div>
                                <Note>
                                    Para exercer qualquer desses direitos, entre em contato pelo e-mail{' '}
                                    <a href={`mailto:${DPO_EMAIL}`} className="text-pacces-gold hover:underline">{DPO_EMAIL}</a>.
                                    Responderemos em até <strong>15 (quinze) dias úteis</strong>, podendo solicitar confirmação de
                                    identidade para garantir a segurança do titular.
                                </Note>
                            </Section>

                            {/* 8. Cookies */}
                            <Section id="cookies" title="8. Cookies e Tecnologias de Rastreamento">
                                <p>
                                    Utilizamos cookies e tecnologias similares para melhorar sua experiência de navegação e
                                    analisar o desempenho do site. Veja as categorias utilizadas:
                                </p>
                                <div className="mt-6 divide-y divide-stone-100 dark:divide-stone-800">
                                    <FinalidadeRow
                                        finalidade="Cookies Essenciais"
                                        descricao="Necessários para o funcionamento básico do site (sessão, segurança). Não podem ser desativados."
                                    />
                                    <FinalidadeRow
                                        finalidade="Cookies de Analytics"
                                        descricao="Google Analytics 4 — medem audiência, páginas mais acessadas e comportamento de navegação. Coletam dados de forma agregada e pseudonimizada."
                                    />
                                    <FinalidadeRow
                                        finalidade="Cookies de Marketing"
                                        descricao="Utilizados para mensurar conversões de campanhas (Google Ads, Meta Ads). Ativados somente mediante consentimento."
                                    />
                                </div>
                                <p className="mt-6">
                                    Você pode gerenciar ou desativar cookies nas configurações do seu navegador. A desativação de
                                    cookies não essenciais não prejudica o acesso ao site, mas pode limitar algumas funcionalidades.
                                    Para saber como gerenciar cookies, consulte as instruções do seu navegador (
                                    <a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="text-pacces-gold hover:underline">Chrome</a>,{' '}
                                    <a href="https://support.mozilla.org/pt-BR/kb/cookies-informacoes-sites-armazenam-no-computador" target="_blank" rel="noopener noreferrer" className="text-pacces-gold hover:underline">Firefox</a>,{' '}
                                    <a href="https://support.apple.com/pt-br/guide/safari/sfri11471/mac" target="_blank" rel="noopener noreferrer" className="text-pacces-gold hover:underline">Safari</a>).
                                </p>
                            </Section>

                            {/* 9. Segurança */}
                            <Section id="seguranca" title="9. Medidas de Segurança">
                                <p>
                                    Adotamos medidas técnicas e organizacionais adequadas para proteger seus dados pessoais contra
                                    acesso não autorizado, perda acidental, destruição ou divulgação indevida, incluindo:
                                </p>
                                <ul className="mt-4 space-y-2 list-none">
                                    <BulletItem>Transmissão dos dados via protocolo HTTPS/TLS</BulletItem>
                                    <BulletItem>Controle de acesso baseado em princípio do menor privilégio</BulletItem>
                                    <BulletItem>Política de senhas e autenticação para sistemas internos</BulletItem>
                                    <BulletItem>Monitoramento contínuo de acessos e incidentes</BulletItem>
                                    <BulletItem>Anonimização de IPs nos relatórios de analytics</BulletItem>
                                </ul>
                                <p className="mt-4">
                                    Em caso de incidente de segurança que possa acarretar risco ou dano relevante aos titulares,
                                    comunicaremos a <strong className="text-stone-700 dark:text-stone-300">Autoridade Nacional de Proteção de Dados (ANPD)</strong> e
                                    os titulares afetados no prazo estabelecido pela legislação vigente.
                                </p>
                            </Section>

                            {/* 10. Transferências Internacionais */}
                            <Section id="transferencias" title="10. Transferências Internacionais de Dados">
                                <p>
                                    Alguns de nossos prestadores de serviço (como Google Analytics) podem processar dados em
                                    servidores localizados fora do Brasil. Nestes casos, exigimos que o tratamento ocorra em
                                    conformidade com o <strong className="text-stone-700 dark:text-stone-300">art. 33 da LGPD</strong>,
                                    garantindo nível de proteção equivalente ao previsto na legislação brasileira por meio de:
                                </p>
                                <ul className="mt-4 space-y-2 list-none">
                                    <BulletItem>Cláusulas contratuais específicas de proteção de dados</BulletItem>
                                    <BulletItem>Transferência para países com grau de proteção adequado reconhecido pela ANPD</BulletItem>
                                    <BulletItem>Certificações e selos de boas práticas reconhecidos internacionalmente</BulletItem>
                                </ul>
                            </Section>

                            {/* 11. Menores */}
                            <Section id="menores" title="11. Menores de Idade">
                                <p>
                                    Este site não é direcionado a menores de 18 (dezoito) anos e não coletamos intencionalmente
                                    dados de crianças ou adolescentes. Caso tomemos conhecimento de que dados de menor de idade
                                    foram coletados sem o devido consentimento de pais ou responsável legal, tomaremos as medidas
                                    necessárias para eliminá-los.
                                </p>
                            </Section>

                            {/* 12. Atualizações */}
                            <Section id="atualizacoes" title="12. Atualizações desta Política">
                                <p>
                                    Esta Política de Privacidade pode ser atualizada periodicamente para refletir mudanças em
                                    nossas práticas, novas exigências legais ou melhorias operacionais. A versão vigente estará
                                    sempre disponível nesta página com a data da última revisão.
                                </p>
                                <p className="mt-4">
                                    Alterações relevantes serão comunicadas por meio de aviso em destaque no site ou, quando
                                    aplicável, por e-mail aos titulares diretamente afetados.
                                </p>
                            </Section>

                            {/* 13. Contato / DPO */}
                            <Section id="contato" title="13. Contato e Encarregado pelo Tratamento (DPO)">
                                <p>
                                    Para exercer seus direitos, esclarecer dúvidas sobre esta Política ou reportar qualquer
                                    questão relacionada ao tratamento de dados pessoais, entre em contato com nosso
                                    <strong className="text-stone-800 dark:text-stone-200"> Encarregado pelo Tratamento de Dados (DPO)</strong>:
                                </p>
                                <InfoBox>
                                    <InfoRow label="E-mail" value={DPO_EMAIL} isLink={`mailto:${DPO_EMAIL}`} />
                                    <InfoRow label="Telefone" value={DPO_PHONE} />
                                    <InfoRow label="Endereço" value={CONTROLLER_ADDRESS} />
                                    <InfoRow label="Horário de Atendimento" value="Segunda a Sexta, das 09h às 18h" />
                                </InfoBox>
                                <p className="mt-6">
                                    Você também pode registrar reclamações perante a{' '}
                                    <strong className="text-stone-700 dark:text-stone-300">Autoridade Nacional de Proteção de Dados (ANPD)</strong>,
                                    por meio do portal{' '}
                                    <a href="https://www.gov.br/anpd" target="_blank" rel="noopener noreferrer" className="text-pacces-gold hover:underline">
                                        www.gov.br/anpd
                                    </a>.
                                </p>
                            </Section>

                        </article>
                    </div>
                </div>

                <LandingFooter />
            </div>
        </PageTransition>
    );
};

/* ─── Sub-componentes de layout ─── */

const Section = ({ id, title, children }: { id: string; title: string; children: React.ReactNode }) => (
    <section id={id} className="scroll-mt-28">
        <div className="flex items-center gap-4 mb-6 pb-4 border-b border-stone-100 dark:border-stone-800">
            <span className="w-6 h-[2px] bg-pacces-gold flex-shrink-0" />
            <h2 className="text-xl font-bold text-stone-900 dark:text-white uppercase tracking-tight font-serif">
                {title}
            </h2>
        </div>
        <div className="space-y-4 text-base font-light leading-relaxed">
            {children}
        </div>
    </section>
);

const SubSection = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <div className="mt-4">
        <h3 className="text-[12px] font-bold uppercase tracking-[0.25em] text-stone-500 dark:text-stone-400 mb-3">
            {title}
        </h3>
        {children}
    </div>
);

const InfoBox = ({ children }: { children: React.ReactNode }) => (
    <div className="mt-6 bg-stone-50 dark:bg-stone-900/40 border border-stone-200 dark:border-stone-800 divide-y divide-stone-200 dark:divide-stone-800">
        {children}
    </div>
);

const InfoRow = ({ label, value, isLink }: { label: string; value: string; isLink?: string }) => (
    <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 px-5 py-3">
        <span className="text-[12px] font-bold uppercase tracking-[0.2em] text-stone-400 dark:text-stone-500 sm:w-48 flex-shrink-0">
            {label}
        </span>
        {isLink ? (
            <a href={isLink} className="text-base text-pacces-gold hover:underline font-light">
                {value}
            </a>
        ) : (
            <span className="text-base text-stone-700 dark:text-stone-300 font-light">{value}</span>
        )}
    </div>
);

const LegalBasis = ({ basis, description }: { basis: string; description: string }) => (
    <div className="flex gap-4 bg-stone-50 dark:bg-stone-900/30 border-l-2 border-pacces-gold/40 px-5 py-4">
        <div>
            <p className="text-[12px] font-bold uppercase tracking-[0.2em] text-pacces-gold mb-1">{basis}</p>
            <p className="text-base font-light leading-relaxed text-stone-600 dark:text-stone-400">{description}</p>
        </div>
    </div>
);

const FinalidadeRow = ({ finalidade, descricao }: { finalidade: string; descricao: string }) => (
    <div className="flex flex-col md:flex-row gap-2 md:gap-8 py-4">
        <span className="text-[12px] font-bold uppercase tracking-[0.2em] text-stone-700 dark:text-stone-300 md:w-52 flex-shrink-0">
            {finalidade}
        </span>
        <span className="text-base font-light text-stone-500 dark:text-stone-400 leading-relaxed">{descricao}</span>
    </div>
);

const RightCard = ({ icon, title, description }: { icon: string; title: string; description: string }) => (
    <div className="group border border-stone-100 dark:border-stone-800 bg-stone-50 dark:bg-stone-900/30 p-5 hover:border-pacces-gold/30 transition-colors duration-300">
        <div className="flex items-start gap-3">
            <span className="material-symbols-outlined text-pacces-gold text-xl flex-shrink-0 mt-0.5">{icon}</span>
            <div>
                <p className="text-[12px] font-bold uppercase tracking-[0.2em] text-stone-700 dark:text-stone-300 mb-1">{title}</p>
                <p className="text-sm font-light text-stone-500 dark:text-stone-400 leading-relaxed">{description}</p>
            </div>
        </div>
    </div>
);

const BulletItem = ({ children }: { children: React.ReactNode }) => (
    <li className="flex items-start gap-3">
        <span className="material-symbols-outlined text-pacces-gold/70 text-base mt-0.5 flex-shrink-0">check_small</span>
        <span className="text-base font-light leading-relaxed text-stone-600 dark:text-stone-400">{children}</span>
    </li>
);

const Note = ({ children }: { children: React.ReactNode }) => (
    <div className="mt-6 flex gap-3 bg-pacces-gold/5 border border-pacces-gold/20 px-5 py-4">
        <span className="material-symbols-outlined text-pacces-gold text-xl flex-shrink-0 mt-0.5">info</span>
        <p className="text-base font-light text-stone-600 dark:text-stone-400 leading-relaxed">{children}</p>
    </div>
);
